// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Modal + toast infrastructure and the static modal content (How it works,
 * Privacy / threat model, About). The modal host is hidden with the `hidden`
 * attribute and the global `[hidden]{display:none!important}` guard, so no
 * invisible overlay can ever swallow taps.
 */
import { icon } from './icons.ts';

let hostEl: HTMLElement | null = null;
let lastFocus: HTMLElement | null = null;

export function initModals(): void {
  const host = document.createElement('div');
  host.id = 'modal-host';
  host.hidden = true;
  host.innerHTML = `<div class="modal-backdrop" data-close></div><div class="modal" role="dialog" aria-modal="true"></div>`;
  document.body.appendChild(host);
  hostEl = host;
  host.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.dataset.close !== undefined || t.closest('[data-close]')) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && hostEl && !hostEl.hidden) closeModal();
  });
}

export function openModal(title: string, bodyHtml: string): void {
  if (!hostEl) return;
  lastFocus = document.activeElement as HTMLElement;
  const modal = hostEl.querySelector('.modal') as HTMLElement;
  modal.innerHTML = `
    <div class="modal-head">
      <h2 class="modal-title">${escapeHtml(title)}</h2>
      <button type="button" class="icon-btn" data-close aria-label="Close">${icon('x')}</button>
    </div>
    <div class="modal-body">${bodyHtml}</div>`;
  hostEl.hidden = false;
  const close = modal.querySelector('[data-close]') as HTMLElement | null;
  close?.focus();
}

export function closeModal(): void {
  if (!hostEl) return;
  hostEl.hidden = true;
  const modal = hostEl.querySelector('.modal') as HTMLElement;
  modal.innerHTML = '';
  lastFocus?.focus?.();
}

export function isModalOpen(): boolean {
  return !!hostEl && !hostEl.hidden;
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toast(msg: string, kind: 'info' | 'ok' | 'err' = 'info'): void {
  let el = document.getElementById('toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'toast';
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.dataset.kind = kind;
  el.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el?.classList.remove('show'), 2600);
}

function g(term: string, label?: string): string {
  return `<span class="glossary-link" data-term="${escapeHtml(term)}" tabindex="0" role="button">${escapeHtml(label ?? term)}</span>`;
}

export function howItWorksHtml(): string {
  return `
    <ol class="steps">
      <li><span class="step-n">1</span><div><strong>Type the secret.</strong>
        A password, an API key, a recovery phrase, a one-time code. Burnwell mints a random room id and a four-word ${g('passphrase', 'passphrase')}, both living only in the link’s <code>#hash</code>, which browsers never send to a server.</div></li>
      <li><span class="step-n">2</span><div><strong>Send the link.</strong>
        Copy it, share it, or show its QR to a phone. Choose <em>full link</em> (passphrase included, most convenient) or <em>${g('split mode', 'split')}</em> (link carries only the room; you read the four-word passphrase aloud separately).</div></li>
      <li><span class="step-n">3</span><div><strong>The two devices find each other.</strong>
        Each browser finds the other through a public ${g('signalling relay', 'signalling relay')} — used once, for the handshake only — then opens a direct ${g('data channel', 'WebRTC data channel')}. No server sits in the secret’s path.</div></li>
      <li><span class="step-n">4</span><div><strong>Confirm the four words.</strong>
        Both devices show the same ${g('sas', 'four-word code')}. If they match, no impostor slipped into the middle.</div></li>
      <li><span class="step-n">5</span><div><strong>Reveal, once.</strong>
        The recipient presses <em>Reveal</em>; the secret is sealed with ${g('aes-gcm', 'AES-GCM-256')}, sent over the channel, and shown once for them to copy.</div></li>
      <li><span class="step-n">6</span><div><strong>Then it burns.</strong>
        The recipient’s confirmation triggers ${g('burn-after-read', 'the burn')}: the secret is wiped from the sender’s device and the room is torn down. A second device that opens the link afterwards gets nothing — ${g('one-time delivery', 'one-time delivery')}.</div></li>
    </ol>
    <p class="muted small">Both people need to be online at the same moment — there’s no server holding the secret for later. That’s the deliberate price of never storing anything anywhere.</p>`;
}

export function privacyHtml(): string {
  return `
    <div class="tm">
      <section><h3 class="tm-good">Protected</h3><ul>
        <li>The <strong>secret</strong> exists as plaintext only inside the two browser tabs. It travels over a ${g('data channel', 'direct data channel')} between them, ${g('dtls', 'DTLS')}-encrypted, and is additionally sealed in ${g('aes-gcm', 'AES-GCM-256')} with a key derived from your ${g('passphrase', 'passphrase')}. No server is ever in the secret’s path — not even to hold ciphertext.</li>
        <li><strong>${g('one-time delivery', 'One-time delivery')}.</strong> The sender releases the secret to exactly one recipient — the first to verify and request it — then ${g('burn-after-read', 'burns')} it. Nothing remains on either device afterwards.</li>
        <li>The <strong>${g('passphrase', 'passphrase')}</strong> encrypts the ${g('signalling relay', 'signalling')} handshake and derives both the secret’s key and the ${g('sas', 'four verification words')}. It lives only in the link’s hash — and in ${g('split mode', 'split mode')} it is never transmitted at all.</li>
        <li><strong>Nothing is stored server-side, ever.</strong> No account, no cookies, no third-party fonts, no history in the cloud.</li>
      </ul></section>
      <section><h3 class="tm-bad">Not protected</h3><ul>
        <li><strong>Both parties must be online at once.</strong> There is no store-and-forward — the secret only moves while both tabs are connected. This is the trade for never storing anything on a server.</li>
        <li>Whoever holds the <strong>full link first</strong> could connect and receive the secret before your intended recipient. Defences: use ${g('split mode', 'split mode')} so the link alone is useless, verify the ${g('sas', 'four words')} to catch a live impostor, and note that one-time delivery means a thief’s success makes the real recipient’s attempt fail — a visible alarm.</li>
        <li>Peer discovery uses <strong>public nostr relays</strong>. They learn an ephemeral room id and that two browsers are connecting, but the handshake is encrypted with your passphrase — they can’t read it or your secret. Your <strong>IP address</strong> is visible to the peer and the public ${g('stun', 'STUN')} server, inherent to any peer-to-peer session.</li>
        <li>A ${g('symmetric nat', 'symmetric-NAT')} pair with no relay may fail to connect. We won’t add a relay because it would be a backend that could see traffic.</li>
        <li>Once revealed, the secret is on the <strong>recipient’s device</strong> — they can copy or screenshot it. Burn means <em>Burnwell</em> keeps no copy, not that the recipient can’t.</li>
      </ul></section>
      <section><h3 class="tm-trust">Trust surface</h3><ul>
        <li>The static site bundle (hash-pinned by the GitHub Pages deploy) and the TLS chain to it.</li>
        <li>The public signalling relays and STUN server (encrypted handshake only; they never carry your secret).</li>
        <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your secret is never sent to it.</li>
        <li>Feedback you choose to send goes to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your secret is never sent.</li>
      </ul></section>
    </div>`;
}

export function aboutHtml(): string {
  return `
    <p>Burnwell is the safe answer to “how do I send someone a password?”. Type a secret, share a one-time link, and it’s handed straight from your browser to theirs — end-to-end encrypted, shown once, then burned. No cloud middleman holds a copy; nothing is stored on any server, ever. It runs entirely in your browser.</p>
    <p>It’s part of a small collection of privacy-first, zero-backend browser tools. Everything happens on your device.</p>
    <p class="about-links">
      <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
      <a href="https://github.com/ben-gy/burnwell" target="_blank" rel="noopener">source on GitHub</a>
    </p>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
